/**
 * Generic "Share feedback" modal (#200, FR-16.2; DI-B categories, DI-E footer→modal). The
 * site-wide feedback surface: pick a category (idea / bug / praise / other), write a note,
 * optionally leave a contact email for a reply (DI-C), and submit in ONE action (FR-9.5).
 * Opened from the footer "Feedback" link on any surface — reachable by guests.
 *
 * Presentational + self-contained: reads the optional signed guest identity and POSTs to
 * the shared `/api/feedback` route (same backend as the post-game card). Feedback text +
 * email never touch the game wire (D200.5). Accessible: a labelled dialog, Escape closes,
 * the thank-you is announced.
 */

import { useEffect, useState } from 'react';
import type { FeedbackType } from '@jeopardy/shared';
import { BRAND_NAME } from './brand';
import { loadIdentity } from '../net/auth';
import { submitFeedback } from '../net/feedback-api';
import { ApiError } from '../net/api-error';

export interface FeedbackModalProps {
  /** Close the modal (backdrop click, ×, Cancel, Escape, or after the thank-you). */
  onClose: () => void;
}

/** The selectable categories (DI-B) with their chip labels. `rating` is post-game only. */
const CATEGORIES: ReadonlyArray<{
  type: Exclude<FeedbackType, 'rating'>;
  label: string;
}> = [
  { type: 'idea', label: '💡 Feature idea' },
  { type: 'bug', label: '🐞 Bug' },
  { type: 'praise', label: '❤️ Praise' },
  { type: 'other', label: '🗨️ Other' },
];

export function FeedbackModal({ onClose }: FeedbackModalProps): React.JSX.Element {
  const [type, setType] = useState<Exclude<FeedbackType, 'rating'>>('idea');
  const [text, setText] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Escape closes the dialog (a11y — matches native dialog behavior).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await submitFeedback(
        {
          type,
          surface: 'generic',
          ...(text.trim() ? { text: text.trim() } : {}),
          ...(email.trim() ? { contactEmail: email.trim() } : {}),
        },
        loadIdentity()?.guestToken,
      );
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not send your feedback. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-scrim"
      data-testid="feedback-modal-scrim"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        data-testid="feedback-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal__x"
          aria-label="Close"
          data-testid="feedback-modal-close"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="modal__title" id="feedback-modal-title">
          Share feedback
        </h3>
        {submitted ? (
          <div
            className="fb-thanks"
            role="status"
            aria-live="polite"
            data-testid="feedback-modal-thanks"
          >
            <span className="fb-thanks__check" aria-hidden="true">
              ✓
            </span>
            <div>
              <div className="fb-thanks__title">Thanks for the feedback!</div>
              <div className="fb-thanks__sub">
                Your note helps us make {BRAND_NAME} better. We read everything.
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="muted">
              Tell us what’s working, what’s not, or request a feature. We read every
              note.
            </p>
            <div className="fb-chips" role="group" aria-label="Feedback type">
              {CATEGORIES.map((c) => (
                <button
                  key={c.type}
                  type="button"
                  className={'chip' + (type === c.type ? ' chip--on' : '')}
                  aria-pressed={type === c.type}
                  data-testid={`feedback-chip-${c.type}`}
                  onClick={() => setType(c.type)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="fb-field">
              <label htmlFor="feedback-modal-text">Your feedback</label>
              <textarea
                id="feedback-modal-text"
                data-testid="feedback-modal-text"
                placeholder="I’d love to be able to…"
                value={text}
                maxLength={2000}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
            <div className="fb-field">
              <label htmlFor="feedback-modal-email">
                Email if you’d like a reply <span className="muted">(optional)</span>
              </label>
              <input
                id="feedback-modal-email"
                type="email"
                data-testid="feedback-modal-email"
                placeholder="you@example.com"
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && (
              <p className="fb-error" role="alert" data-testid="feedback-modal-error">
                {error}
              </p>
            )}
            <div className="fb-actions">
              <button
                type="button"
                className="btn btn--primary"
                data-testid="feedback-modal-send"
                disabled={submitting || text.trim().length === 0}
                onClick={() => void send()}
              >
                {submitting ? 'Sending…' : 'Send'}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                data-testid="feedback-modal-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
